/**
 * <hydrate-filters> — Layer 3 consumer custom element.
 *
 * Renders dynamic filter groups and sort controls from the APIM product-search
 * response. Uses two separate slide-in drawers (Sort + Filter). Selections
 * commit immediately on change and auto-close the drawer (MA-1419); each
 * change triggers a re-fetch via hydratePLP /
 * hydrateSLP.
 *
 * Architecture position:
 *   Layer 1: query-product-list.js  (raw APIM fetch)
 *   Layer 2: hydrate-callers.js     (orchestration + event dispatch)
 *   Layer 3: hydrate-filters.js  (this module — filter/sort UI + re-fetch trigger)
 *
 * Data source:
 *   - `window.__ngHydration` — cached result (race-condition guard).
 *   - `theme:search:products:hydrated` CustomEvent — the eager per-module
 *     event dispatched the moment the product search resolves, so the filter
 *     bar paints without waiting for the store search (MA-1367).
 *   - `theme:search:hydrated` CustomEvent on `document` — the combined event;
 *     an envelope already processed from the eager event is skipped by
 *     identity.
 *
 * Filter data shape (raw APIM `filters` object, passed through by
 * normalise-response.js):
 *   envelope.data.filters = {
 *     regularFilters: [{
 *       name: 'Brand' | 'Flavors' | …,
 *       uiType: 'checkbox' | 'radio',
 *       filterType: 'brand' | 'price' | 'stores' | 'sort' | 'isOnSale'
 *                 | 'Attribute' | 'Department' | …,
 *       nodeId?: string,   // present on category/attribute groups
 *       count: number,
 *       // exactly ONE of the following (or none for boolean checkbox toggles):
 *       filterOptions?:      [{ value, identifier, count }]   // brand, price
 *                          | [{ value, nodeId, count }],      // Attribute groups
 *       storeFilterOptions?: [{ catalogId, catalogName, count }],
 *       sortFilterOptions?:  [{ value, identifier }],
 *       departmentOptions?:  [{ name, filterType: 'Category', nodeId,
 *                               filterOptions: [{ value, nodeId, count }] }]
 *     }],
 *     filtersApplied: [...]
 *   }
 *
 * Notes:
 *   - Checkbox groups WITHOUT an options array (e.g. Sale, FSA/HSA Eligible,
 *     Shipping Offers, Exclusive Brands) are boolean toggles — the group itself
 *     is the filter.
 *   - The group with filterType 'sort' is NOT a filter — its sortFilterOptions
 *     feed the sort drawer radio list.
 *   - MANY groups share filterType 'Attribute' (Flavors, Ingredients, …); they
 *     are disambiguated by their group-level `nodeId`.
 *   - Options carry EITHER `identifier` (brand/price — sent under filterType)
 *     OR `nodeId` (category/attribute — sent under the `node.id` request key).
 *   - `value` strings carry HTML entities (&reg;, &trade;) — decoded for
 *     display; raw identifiers / nodeIds are sent back to the API.
 */

import { htmlEscape, decodeHtmlEntities } from '@theme/utilities';
import { hydratePLP, hydrateSLP, FILTER_AGGREGATIONS, productFiltersFromParams, NON_FILTER_PARAMS } from '@theme/hydrate-callers';

/**
 * Fallback sort options used until the APIM response provides its own
 * `sortFilterOptions` (filterType 'sort' in regularFilters).
 */
const DEFAULT_SORT_OPTIONS = [
  { identifier: 'best-match', label: 'Best Match' },
  { identifier: 'most-popular', label: 'Most Popular' },
  { identifier: 'recent-arrivals', label: 'Recent Arrivals' },
  { identifier: 'price-low-high', label: 'Price - Low to High' },
  { identifier: 'price-high-low', label: 'Price - High to Low' },
];

const DEFAULT_SORT_VALUE = 'best-match';

/**
 * Sort identifiers hidden from distributor (PPC/UFO) customers. Cashback is a
 * retail-only benefit, so the "Most Cashback" sort is not relevant to them.
 */
const DISTRIBUTOR_HIDDEN_SORTS = ['most-cashback'];

/**
 * Reads distributor status from the `<meta name="ma:customer">` tag rendered by
 * `snippets/meta-tags.liquid`. A logged-in PPC/UFO customer (`data-is-ppc`) is
 * treated as a distributor.
 *
 * @returns {boolean}
 */
const isDistributorCustomer = () => {
  const meta = document.querySelector('meta[name="ma:customer"]');
  return meta instanceof HTMLElement && meta.dataset.isPpc === 'true';
};

/* ---------------------------------------------------------------------------
 * Tailwind class strings for JS-rendered drawer markup.
 * Kept here (rather than a {% stylesheet %} block) so styling lives in utility
 * classes. New tokens require `npm run build:css` to appear in tailwind.css.
 * ------------------------------------------------------------------------- */

/** Shared row geometry — sort options, boolean toggles, group rows, options. */
const ROW_CLS =
  'tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-3 tw:lg:px-7 tw:lg:py-4 tw:px-5 tw:py-3 ' +
  'tw:border-b tw:border-b-[rgb(var(--color-foreground-rgb)/0.20)] ' +
  'tw:lg:text-[18px] tw:text-base tw:leading-[1.2] tw:cursor-pointer tw:hover:bg-[#ededed]';

/** Visually-hidden native input that drives the indicator via the peer pattern. */
const INPUT_CLS = 'tw:peer tw:absolute tw:opacity-0 tw:pointer-events-none';

/** Mobile 16px / desktop 24px checkbox indicator with scaled checked fill. */
const BOX_CLS =
  'tw:relative tw:flex tw:items-center tw:justify-center tw:w-4 tw:h-4 tw:shrink-0 tw:lg:w-6 tw:lg:h-6 ' +
  'tw:lg:rounded-full tw:rounded-full tw:border tw:border-[#D8D7D8] ' +
  "tw:peer-checked:border-black tw:peer-checked:after:content-[''] tw:peer-checked:after:block " +
  'tw:peer-checked:after:w-2.5 tw:peer-checked:after:h-2.5 tw:peer-checked:after:rounded-full tw:peer-checked:after:bg-black ' +
  'tw:lg:peer-checked:after:w-4 tw:lg:peer-checked:after:h-4 tw:lg:peer-checked:after:rounded-full ' +
  'tw:peer-focus-visible:outline tw:peer-focus-visible:outline-2 tw:peer-focus-visible:outline-offset-2';

/** Sort radio reuses the box but turns circular with #211E24 fill on mobile. */
const RADIO_CLS =
  BOX_CLS +
  ' tw:rounded-full tw:peer-checked:border-[#211E24] ' +
  'tw:peer-checked:after:rounded-full tw:peer-checked:after:w-2 ' +
  'tw:peer-checked:after:h-2 tw:peer-checked:after:bg-[#211E24] ' +
  'tw:lg:rounded-full tw:lg:peer-checked:border-black ' +
  'tw:lg:peer-checked:after:rounded-full tw:lg:peer-checked:after:w-4 ' +
  'tw:lg:peer-checked:after:h-4 tw:lg:peer-checked:after:bg-black';

/** Group navigation row (a full-width button) — row geometry + button reset. */
const GROUP_BTN_CLS =
  ROW_CLS +
  ' tw:w-full tw:bg-transparent tw:border-x-0 tw:border-t-0 tw:text-start tw:select-none ' +
  'tw:font-[inherit] tw:text-[var(--color-foreground)] tw:hover:bg-[#ededed]';

/** Non-interactive category header inside a grouped sub-drawer (Department). */
const SUBGROUP_HEADER_CLS =
  'tw:flex tw:w-full tw:items-center tw:lg:px-7 tw:lg:py-4 tw:px-5 tw:py-3 ' +
  'tw:border-b tw:border-b-[rgb(var(--color-foreground-rgb)/0.20)] ' +
  'tw:lg:text-[18px] tw:text-base tw:leading-[1.2] tw:font-medium tw:select-none';

/** Selectable category header row in a grouped sub-drawer (bold option row). */
const SUBGROUP_HEADER_ROW_CLS = ROW_CLS + ' tw:font-medium';

/** Option row indented under a category header in a grouped sub-drawer. */
const SUBGROUP_OPTION_CLS = ROW_CLS + ' tw:lg:pl-12 tw:pl-9';

/** Active filter chip (removable) — filled pill with #F0F1F8 background. */
const CHIP_CLS =
  'tw:inline-flex tw:items-center tw:gap-3 tw:px-4 tw:py-1.5 tw:rounded-[70px] tw:bg-[#F0F1F8] tw:border-0 ' +
  'tw:font-[inherit] tw:text-[12px] tw:font-medium tw:leading-[16px] tw:text-black tw:cursor-pointer ' +
  'tw:hover:bg-[#e4e5f0]';

/**
 * Fixed (non-removable) base-node pill — same look as a committed chip but with
 * no close affordance or hover, since the collection's base node IDs define the
 * PLP scope and must not be cleared by the shopper.
 */
const FIXED_CHIP_CLS =
  'tw:inline-flex tw:items-center tw:gap-3 tw:px-4 tw:py-1.5 tw:rounded-[70px] tw:bg-[#F0F1F8] tw:border-0 ' +
  'tw:font-[inherit] tw:text-[12px] tw:font-medium tw:leading-[16px] tw:text-black';

/**
 * "clear filters" underlined text button next to the chips.
 * MA-1705: hidden on mobile per design (Boris) — the mobile chips row drops the
 * Clear All CTA; it stays on desktop (lg+).
 */
const CLEAR_ALL_CLS =
  'tw:hidden tw:lg:inline-flex tw:items-center tw:border-0 tw:bg-transparent ' +
  'tw:font-[inherit] tw:text-[12px] tw:leading-[16px] tw:text-black tw:underline tw:cursor-pointer tw:hover:opacity-70';

/** Dynamic group pill button (JS-rendered, one per filter group). */
const PILL_CLS =
  'tw:inline-flex tw:items-center tw:justify-center tw:h-7 tw:lg:h-[39px] tw:px-3 tw:lg:px-4 ' +
  'tw:rounded-[70px] tw:border tw:border-[#CBCBCB] tw:bg-transparent ' +
  'tw:text-[12px] tw:lg:text-[14px] tw:font-light tw:lg:font-medium tw:leading-[16px] tw:lg:leading-[18px] ' +
  'tw:text-black tw:whitespace-nowrap tw:shrink-0 tw:cursor-pointer tw:font-[inherit] ' +
  'tw:hover:bg-[#f5f5f5] tw:transition-colors';

/** Active state modifier — border turns black when the group has committed selections. */
const PILL_ACTIVE_CLS = 'tw:border-black';

/**
 * Deep-clones a Map<string, Map<string, string>> to prevent staged mutations
 * from leaking into committed state.
 *
 * @param {Map<string, Map<string, string>>} source
 * @returns {Map<string, Map<string, string>>}
 */
const cloneFilterMap = (source) =>
  new Map(Array.from(source, ([k, v]) => [k, new Map(v)]));

/**
 * @typedef {Object} NormalisedOption
 * @property {string} value  - Raw value sent back to the API (nodeId for
 *                             category/attribute facets, identifier for
 *                             brand/price, catalogId for stores).
 * @property {string} label  - Entity-decoded display label.
 * @property {number} [count] - Result count for the option, if provided.
 *
 * @typedef {Object} NormalisedGroup
 * @property {string}  key        - Unique internal id (multiple groups share
 *                                  filterType 'Attribute', so this is derived
 *                                  from the group's nodeId when present).
 * @property {string}  name       - Entity-decoded group display name.
 * @property {string}  requestKey - Key used in the request `filters` payload
 *                                  ('node.id' for nodeId facets, 'catalogId'
 *                                  for stores, or the raw filterType for
 *                                  brand/price/booleans).
 * @property {string}  uiType     - 'checkbox' | 'radio' from the API.
 * @property {number}  [count]    - Group-level result count (boolean toggles).
 * @property {Array<NormalisedOption>|null} options - Options, or null for boolean toggles.
 * @property {Array<{ name: string, value: string, count?: number, options: Array<NormalisedOption> }>} [optionGroups]
 *                                  - Sub-grouped options (Department: one entry
 *                                    per category). `value` is the category's
 *                                    own selectable nodeId. When present, the
 *                                    sub-drawer renders selectable category
 *                                    headers + indented subcategory options.
 * @property {boolean} isBoolean  - True when the group itself is the filter.
 */

class HydrateFilters extends HTMLElement {
  /** @type {((event: Event) => void) | null} */
  #onHydrated = null;

  /** @type {((event: Event) => void) | null} */
  #onLoading = null;

  /** Envelope already processed from the eager per-module event — the
   * combined `theme:search:hydrated` event re-delivers the same object and
   * is skipped by identity (URL restore/prune must only run once per fetch).
   * @type {Object | null} */
  #lastHandledEnvelope = null;

  /** @type {((event: Event) => void) | null} */
  #onPageChange = null;

  /** @type {((event: KeyboardEvent) => void) | null} */
  #onKeydown = null;

  // ---- Committed state (drives chips + refetch payload) ----

  /** @type {Map<string, Map<string, string>>} group key → (raw value → display label) */
  #selectedFilters = new Map();

  /** @type {Map<string, NormalisedGroup>} group key → group meta (accumulated across renders) */
  #groupMeta = new Map();

  /** @type {Array<{ identifier: string, label: string }>} */
  #sortOptions = DEFAULT_SORT_OPTIONS;

  /** @type {string} */
  #currentSort = DEFAULT_SORT_VALUE;

  // ---- Staged state (tentative inside an open drawer) ----

  /** @type {Map<string, Map<string, string>>} */
  #stagedFilters = new Map();

  /** @type {string} */
  #stagedSort = DEFAULT_SORT_VALUE;

  // ---- Cached filter groups (for re-rendering drawer bodies) ----

  /** @type {Array<NormalisedGroup>} */
  #filterGroups = [];

  // ---- Base node IDs from collection metafield ----

  /** @type {Array<string>} */
  #baseNodeIds = [];

  /**
   * Display labels for the base node IDs, resolved from the API response's
   * `filtersApplied` (falls back to the raw node ID when the API returns no
   * matching applied filter). Populated once per node ID and reused across
   * re-fetches.
   * @type {Map<string, string>}
   */
  #baseNodeLabels = new Map();

  /**
   * Predefined search term from the collection metafield `custom.search_term`
   * (PLP only). When set it wins over the base node IDs: it is sent as the
   * API `query` on every re-fetch and the node IDs are ignored. It is never
   * shown in the UI (no chip, no "results for" copy) nor written to the URL.
   * @type {string}
   */
  #baseSearchTerm = '';

  // ---- Misc ----

  /** @type {'plp' | 'slp'} */
  #context = 'plp';

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number} */
  #currentPage = 1;

  /** @type {number} */
  #totalCount = 0;

  /** @type {boolean} */
  #showSort = true;

  /** @type {boolean} */
  #showProductCount = true;

  /** @type {'sort' | 'filter' | null} */
  #activeDrawer = null;

  /** Whether the filter sub-drawer (single option group) is open. */
  #subOpen = false;

  /** @type {string | null} group key currently shown in the sub-drawer */
  #activeGroupKey = null;

  /** @type {HTMLElement | null} */
  #lastTrigger = null;

  /**
   * True until the first hydration response after a reload/shared link, when
   * committed filter selections (chips + drawer state) are rebuilt from the URL.
   * @type {boolean}
   */
  #needsUrlRestore = false;

  /**
   * True when the sub-drawer was opened directly from a toolbar group pill
   * (instead of via the full filter drawer). Changes close/apply/clear
   * behaviour so the sub-drawer operates independently.
   * @type {boolean}
   */
  #pillDirectOpen = false;

  /** Whether the passive scroll listener on the pills row has been attached. */
  #scrollListenerAttached = false;

  /**
   * Observes the group-pills row so the scroll arrows are re-measured whenever
   * its size changes — including the hidden→visible reveal on the first
   * hydration response (when scrollWidth/clientWidth would otherwise read 0)
   * and viewport resizes.
   * @type {ResizeObserver | null}
   */
  #resizeObserver = null;

  /**
   * User-facing strings, hydrated from `data-i18n-*` attributes on connect so
   * all copy stays in the locale files (English fallbacks kept inline for
   * resilience if an attribute is missing).
   * @type {{ results: string, resultsFor: string, clearFilters: string }}
   */
  #i18n = {
    results: 'Results',
    resultsFor: 'Results for',
    clearFilters: 'clear filters',
    priceMin: 'Min',
    priceMax: 'Max',
    priceCustomRange: 'Custom Range',
  };

  /** Currency symbol read from `data-currency-symbol` (defaults to '$'). */
  #currencySymbol = '$';

  /**
   * The overlay + drawers wrapper, relocated to <body> on connect so its
   * fixed-position children escape the <main> stacking context (otherwise the
   * sticky header sits on top of the backdrop) and slide reliably from the
   * inline-end edge on every viewport.
   * @type {HTMLElement | null}
   */
  #portal = null;

  connectedCallback() {
    this.#context = /** @type {'plp' | 'slp'} */ (this.getAttribute('data-context') || 'plp');
    this.#showSort = this.getAttribute('data-show-sort') !== 'false';
    this.#showProductCount = this.getAttribute('data-show-product-count') !== 'false';

    // Read the collection metafield search term from the data attribute.
    // When set it drives every PLP re-fetch as the API `query`, and the base
    // node IDs are ignored entirely (no filter merge, no fixed chips).
    this.#baseSearchTerm = (this.getAttribute('data-search-term') || '').trim();

    // Read the collection metafield node IDs from the data attribute.
    // These are the base node IDs that must be included in every PLP re-fetch.
    const nodeIdAttr = this.#baseSearchTerm ? '' : (this.getAttribute('data-filter-node-ids') || '');
    this.#baseNodeIds = nodeIdAttr.split(',').map(id => id.trim()).filter(Boolean);

    // Pull translated UI copy from data-i18n-* attributes (set in Liquid via
    // the | t filter), falling back to the inline English defaults.
    const ds = this.dataset;
    this.#i18n = {
      results: ds.i18nResults || this.#i18n.results,
      resultsFor: ds.i18nResultsFor || this.#i18n.resultsFor,
      clearFilters: ds.i18nClearFilters || this.#i18n.clearFilters,
      priceMin: ds.i18nPriceMin || this.#i18n.priceMin,
      priceMax: ds.i18nPriceMax || this.#i18n.priceMax,
      priceCustomRange: ds.i18nPriceCustomRange || this.#i18n.priceCustomRange,
    };
    this.#currencySymbol = ds.currencySymbol || this.#currencySymbol;

    // Translate the fallback sort-option labels (used until the API supplies
    // its own sortFilterOptions) from data attributes, keyed by identifier.
    const sortLabelI18n = {
      'best-match': ds.i18nSortBestMatch,
      'most-popular': ds.i18nSortMostPopular,
      'recent-arrivals': ds.i18nSortRecentArrivals,
      'price-low-high': ds.i18nSortPriceLowHigh,
      'price-high-low': ds.i18nSortPriceHighLow,
    };
    this.#sortOptions = DEFAULT_SORT_OPTIONS.map((opt) => ({
      identifier: opt.identifier,
      label: sortLabelI18n[opt.identifier] || opt.label,
    }));

    // Initialise #currentPage from the URL so it matches the bootstrap fetch's
    // pageParam (hydrate-callers reads the same param). Without this, the first
    // filter/sort refetch after a page-2+ reload would incorrectly use page 1.
    this.#currentPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10) || 1;

    // Restore applied sort/filters from the URL so a reloaded or shared link
    // keeps the drawer UI in sync with the (already filtered) bootstrap fetch.
    // Sort can be applied immediately; filter selections need group metadata
    // from the first response, so defer them via #needsUrlRestore.
    const { filters: urlFilters, sort: urlSort } = productFiltersFromParams(
      new URLSearchParams(window.location.search)
    );
    if (urlSort) {
      this.#currentSort = urlSort;
      this.#stagedSort = urlSort;
    }
    // Defer to the first response (which carries the available filter/sort
    // metadata) whenever the URL applies a filter OR a non-default sort — so an
    // unavailable filter/sort can be pruned and re-fetched without it.
    this.#needsUrlRestore =
      Object.keys(urlFilters).length > 0 || (urlSort != null && urlSort !== DEFAULT_SORT_VALUE);

    // Relocate the drawer portal to <body> so fixed positioning is resolved
    // against the viewport and the backdrop overlays the sticky header.
    this.#portal = this.querySelector('[data-ngf-portal]');
    if (this.#portal) {
      this.#portal.hidden = false;
      document.body.appendChild(this.#portal);
      // Delegated listeners on the portal (it no longer bubbles up to `this`).
      this.#portal.addEventListener('change', (e) => this.#handleChange(e));
      this.#portal.addEventListener('click', (e) => this.#handleClick(e));
      this.#portal.addEventListener('input', (e) => this.#handlePriceSliderInput(e));
    }

    // Event delegation for the toolbar triggers (still inside `this`)
    this.addEventListener('change', (e) => this.#handleChange(e));
    this.addEventListener('click', (e) => this.#handleClick(e));

    // Escape key closes the topmost open layer (sub-drawer first, then drawer)
    this.#onKeydown = (e) => {
      if (e.key !== 'Escape') return;
      if (this.#subOpen) {
        this.#closeSubDrawer();
      } else if (this.#activeDrawer) {
        this.#closeActiveDrawer();
      }
    };
    document.addEventListener('keydown', this.#onKeydown);

    this.#onHydrated = (event) => {
      const detail = /** @type {CustomEvent} */ (event).detail;
      this.#handleResult(detail);
    };
    // Eager per-module event (products resolved — stores may still be in
    // flight) plus the combined event (re-fetches, race guard).
    document.addEventListener('theme:search:products:hydrated', this.#onHydrated);
    document.addEventListener('theme:search:hydrated', this.#onHydrated);

    // Show the skeleton toolbar while a fetch for this context is in flight, so
    // the filter/sort bar reserves its space instead of popping in when data
    // arrives (mirrors the product-grid skeleton).
    this.#onLoading = (event) => {
      if (/** @type {CustomEvent} */ (event).detail?.context === this.#context) this.#showLoading();
    };
    document.addEventListener('theme:search:loading', this.#onLoading);

    // Listen for pagination page-change events from the products section.
    // When present, the filters section owns the re-fetch (preserving
    // filter + sort state) and signals this by calling preventDefault().
    this.#onPageChange = (event) => {
      const detail = /** @type {CustomEvent} */ (event).detail;
      if (!detail || detail.context !== this.#context) return;

      event.preventDefault();
      this.#currentPage = detail.page || 1;
      this.#refetch();
    };
    document.addEventListener('theme:page:change', this.#onPageChange);

    // Initialise the mobile grid/list view toggle button states from the
    // persisted preference (products read the same localStorage key on connect).
    this.#initViewToggle();

    // Race guard: if hydration resolved before this element connected, the
    // event was missed — process the cached result now.
    if (window.__ngProductHydration) {
      this.#handleResult(window.__ngProductHydration);
    } else if (window.__ngHydration) {
      this.#handleResult(window.__ngHydration);
    } else if (this.#isDesignMode) {
      this.#showEmptyState();
    } else {
      this.#showLoading();
    }
  }

  disconnectedCallback() {
    if (this.#onLoading) {
      document.removeEventListener('theme:search:loading', this.#onLoading);
    }
    if (this.#onHydrated) {
      document.removeEventListener('theme:search:products:hydrated', this.#onHydrated);
      document.removeEventListener('theme:search:hydrated', this.#onHydrated);
    }

    if (this.#onPageChange) {
      document.removeEventListener('theme:page:change', this.#onPageChange);
    }

    if (this.#onKeydown) {
      document.removeEventListener('keydown', this.#onKeydown);
    }

    if (this.#abortController) {
      this.#abortController.abort();
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }

    // Ensure body scroll is restored
    if (this.#activeDrawer) {
      document.body.style.overflow = '';
      document.body.classList.remove('ngf-drawer-open');
    }

    // Remove the relocated portal from <body>
    if (this.#portal && this.#portal.parentNode) {
      this.#portal.parentNode.removeChild(this.#portal);
    }
    this.#portal = null;
  }

  /**
   * Queries inside the relocated portal (drawers/overlay). Falls back to `this`
   * before the portal is established.
   * @param {string} selector
   * @returns {HTMLElement | null}
   */
  #q(selector) {
    return /** @type {HTMLElement | null} */ ((this.#portal || this).querySelector(selector));
  }

  /** Whether the storefront is viewed in the Shopify theme editor. */
  get #isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  /**
   * Processes a hydration result. Only acts on results matching this
   * element's context ('plp' or 'slp').
   *
   * @param {{ context?: string, products?: { ok?: boolean, data?: { totalCount?: number, filters?: { regularFilters?: Array, filtersApplied?: Array } }, error?: { message?: string } } } | null | undefined} result
   */
  #handleResult(result) {
    if (!result || result.context !== this.#context) return;

    const envelope = result.products;

    // Combined event re-delivering the envelope already processed from the
    // eager per-module event — nothing new to do.
    if (envelope && envelope === this.#lastHandledEnvelope) return;

    if (!envelope || !envelope.ok) {
      this.#showEmptyState();
      return;
    }

    this.#lastHandledEnvelope = envelope;

    const data = envelope.data;

    // Related-search fallback — the API returned related products because the
    // original query had zero exact matches. Filters/sorting are not applicable
    // to a related-search result set, so collapse the toolbar entirely.
    if (data?.messages?.relatedSearchApplied?.isZeroResultOnOriginalSearch) {
      this.#showEmptyState();
      return;
    }

    this.#totalCount = data?.totalCount ?? 0;
    const { filterGroups, sortOptions } = this.#normaliseGroups(data?.filters);

    if (sortOptions) {
      // Distributors (PPC/UFO) don't get the retail-only "Most Cashback" sort.
      this.#sortOptions = isDistributorCustomer()
        ? sortOptions.filter((opt) => !DISTRIBUTOR_HIDDEN_SORTS.includes(opt.identifier))
        : sortOptions;
    }

    this.#filterGroups = filterGroups;

    // Resolve human-readable labels for the collection's base node IDs from the
    // API's `filtersApplied`, so each base node renders as a fixed applied pill.
    this.#resolveBaseNodeLabels(data?.filters);

    if (filterGroups.length === 0 && !this.#showSort) {
      this.#showEmptyState();
      return;
    }

    // First response after a reload/shared link — rebuild committed selections
    // (chips + drawer checkboxes) from the URL now that group metadata is known.
    let prunedAfterRestore = false;
    if (this.#needsUrlRestore) {
      // The API's `filtersApplied` echo is the source of truth for whether a
      // URL param was respected — an applied value can be absent from
      // `regularFilters` (e.g. the Category facet group is dropped from the
      // response once a category node is applied, offering only drill-down
      // within it). Validating against offered options alone would wrongly
      // discard such filters (e.g. /search?q=*&node.id=1525854 — Face Makeup).
      const appliedByKey = this.#appliedFiltersByRequestKey(data?.filters);
      this.#restoreSelectionsFromUrl(filterGroups, appliedByKey);
      // Drop any filter/sort param the response neither offers nor applied,
      // so a stale/shared link reflects only what's applicable after reload.
      prunedAfterRestore = this.#pruneUrlToResponse(filterGroups, appliedByKey);
      this.#needsUrlRestore = false;
    }

    // Zero results with no committed filters: filtering or sorting an empty
    // result set cannot change the outcome, so hide the controls entirely.
    // When the customer's own filters caused the empty state, the pills and
    // chips stay so they can clear them. Applies to PLP and SLP alike.
    if (this.#totalCount === 0 && this.#selectedFilters.size === 0) {
      this.#showEmptyState();
      return;
    }

    this.#render(filterGroups);
    this.hidden = false;

    // A param was pruned — the initial (bootstrap) fetch still applied the stale
    // filter/sort, so re-query with the cleaned state to align the results.
    if (prunedAfterRestore) {
      this.#refetch();
    }
  }

  /**
   * Removes filter/sort query params that the current response neither offers
   * as a facet option nor confirms as applied, then rewrites the URL from the
   * (now-pruned) committed state.
   *
   * @param {Array<NormalisedGroup>} filterGroups
   * @param {Map<string, Map<string, { label: string, groupName: string }>>} [appliedByKey]
   *   Applied filters echoed by the response, from #appliedFiltersByRequestKey.
   * @returns {boolean} Whether any param was actually removed (caller re-fetches).
   */
  #pruneUrlToResponse(filterGroups, appliedByKey) {
    // An applied sort that the response no longer offers reverts to default.
    if (
      this.#currentSort !== DEFAULT_SORT_VALUE &&
      !this.#sortOptions.some((opt) => opt.identifier === this.#currentSort)
    ) {
      this.#currentSort = DEFAULT_SORT_VALUE;
      this.#stagedSort = DEFAULT_SORT_VALUE;
    }

    // Valid values per request key, derived from the response groups.
    /** @type {Map<string, Set<string>>} */
    const validByKey = new Map();
    for (const group of filterGroups) {
      const set = validByKey.get(group.requestKey) || new Set();
      if (group.isBoolean) {
        set.add('true');
      } else {
        for (const opt of group.options || []) set.add(opt.value);
      }
      validByKey.set(group.requestKey, set);
    }

    const url = new URL(window.location.href);
    const sp = url.searchParams;
    let changed = false;

    // Drop the sort param if it isn't an available (non-default) sort.
    const urlSort = sp.get('sort');
    if (urlSort && urlSort !== this.#currentSort) {
      sp.delete('sort');
      changed = true;
    }

    // For every filter param, keep only values the response still offers as
    // an option OR echoes in `filtersApplied` — an applied category node stays
    // valid even though its facet group is no longer offered.
    for (const key of [...new Set(sp.keys())]) {
      if (key === 'sort' || NON_FILTER_PARAMS.has(key) || key.startsWith('utm_')) continue;

      const valid = validByKey.get(key);
      const applied = appliedByKey?.get(key);
      const kept = sp.getAll(key).filter((value) => valid?.has(value) || applied?.has(value));

      if (kept.length !== sp.getAll(key).length) {
        sp.delete(key);
        for (const value of kept) sp.append(key, value);
        changed = true;
      }
    }

    if (changed) history.replaceState(history.state, '', url);

    return changed;
  }

  /**
   * Collects the filters the API confirms it applied (`filtersApplied`),
   * keyed by the URL/request param they are sent under. Used alongside the
   * offered options when restoring/pruning URL params: a value the API
   * applied is valid even when no `regularFilters` group offers it — the
   * Category/Department facet group is dropped from the response once a
   * category node is applied (only drill-down within it is offered).
   *
   * @param {{ filtersApplied?: Array<any> } | null | undefined} filterData
   * @returns {Map<string, Map<string, { label: string, groupName: string }>>}
   *   requestKey → value → { label, groupName }, labels entity-decoded.
   */
  #appliedFiltersByRequestKey(filterData) {
    /** @type {Map<string, Map<string, { label: string, groupName: string }>>} */
    const applied = new Map();
    const groups = Array.isArray(filterData?.filtersApplied) ? filterData.filtersApplied : [];

    for (const group of groups) {
      if (group == null || typeof group !== 'object') continue;
      const groupName = decodeHtmlEntities(String(group.name ?? group.filterType ?? ''));

      // The applied-options array's property name varies by group type
      // (`selectedOptions` for node groups) — accept any array-valued
      // property, like #resolveBaseNodeLabels does.
      const options = Object.values(group).filter(Array.isArray).flat();

      for (const opt of options) {
        if (opt == null || typeof opt !== 'object') continue;

        // Mirror #normaliseGroups' per-kind value resolution: node → nodeId
        // under `node.id`, store → catalogId under `catalogId`, everything
        // else → identifier/value under the group's own filterType.
        let requestKey;
        let value;
        let label;
        if (opt.nodeId != null && opt.nodeId !== '') {
          requestKey = 'node.id';
          value = String(opt.nodeId);
          label = String(opt.value ?? opt.nodeId);
        } else if (opt.catalogId != null && opt.catalogId !== '') {
          requestKey = 'catalogId';
          value = String(opt.catalogId);
          label = String(opt.catalogName ?? opt.catalogId);
        } else {
          requestKey = String(group.filterType ?? '');
          value = String(opt.identifier ?? opt.value ?? '');
          label = String(opt.value ?? opt.identifier ?? '');
        }
        if (!requestKey || !value) continue;

        const values = applied.get(requestKey) || new Map();
        if (!values.has(value)) {
          values.set(value, { label: decodeHtmlEntities(label), groupName });
        }
        applied.set(requestKey, values);
      }
    }

    return applied;
  }

  /**
   * Resolves display labels for the collection's base node IDs (PLP only) by
   * scanning the API response's `filtersApplied` array — the API echoes the
   * node IDs it applied, carrying their human-readable names. Falls back to
   * `regularFilters` and, failing that, leaves the ID unresolved so the raw ID
   * is shown. Each ID is resolved at most once and cached across re-fetches.
   *
   * @param {{ filtersApplied?: Array<Object>, regularFilters?: Array<Object> } | null | undefined} filterData
   */
  #resolveBaseNodeLabels(filterData) {
    if (this.#context !== 'plp' || this.#baseNodeIds.length === 0) return;

    const remaining = new Set(this.#baseNodeIds.filter((id) => !this.#baseNodeLabels.has(id)));
    if (remaining.size === 0 || !filterData) return;

    // The exact `filtersApplied` shape varies by group type, so walk the
    // structure and match on `nodeId`/`identifier`/`value` rather than assuming
    // a fixed layout. The first label found for an ID wins.
    const visit = (node) => {
      if (remaining.size === 0 || node == null || typeof node !== 'object') return;

      if (Array.isArray(node)) {
        for (const item of node) visit(item);
        return;
      }

      const id = node.nodeId != null ? String(node.nodeId)
        : node.identifier != null ? String(node.identifier)
        : '';
      if (id && remaining.has(id)) {
        const label = decodeHtmlEntities(String(node.value ?? node.name ?? node.label ?? id));
        this.#baseNodeLabels.set(id, label);
        remaining.delete(id);
      }

      for (const key of Object.keys(node)) {
        const value = node[key];
        if (value && typeof value === 'object') visit(value);
      }
    };

    visit(filterData.filtersApplied);
    if (remaining.size > 0) visit(filterData.regularFilters);
  }

  /**
   * Maps the raw APIM `filters` object into renderable groups + sort options.
   *
   * @param {{ regularFilters?: Array<Object> } | null | undefined} filterData
   * @returns {{ filterGroups: Array<NormalisedGroup>, sortOptions: Array<{ identifier: string, label: string }> | null }}
   */
  #normaliseGroups(filterData) {
    const raw = Array.isArray(filterData?.regularFilters) ? filterData.regularFilters : [];

    /** @type {Array<NormalisedGroup>} */
    const filterGroups = [];
    /** @type {Array<{ identifier: string, label: string }> | null} */
    let sortOptions = null;

    /**
     * Builds + pushes an option group from a raw group/department node.
     *
     * @param {Object} src - Raw group or departmentOption carrying the options.
     * @param {Array<Object>} rawOptions
     * @param {'node'|'store'|'identifier'} kind
     */
    const pushOptionGroup = (src, rawOptions, kind) => {
      const options = rawOptions
        .map((opt) => {
          const count = typeof opt.count === 'number' ? opt.count : undefined;

          if (kind === 'node') {
            return {
              value: String(opt.nodeId ?? ''),
              label: decodeHtmlEntities(String(opt.value ?? opt.nodeId ?? '')),
              count,
            };
          }
          if (kind === 'store') {
            return {
              value: String(opt.catalogId ?? ''),
              label: decodeHtmlEntities(String(opt.catalogName ?? opt.catalogId ?? '')),
              count,
            };
          }
          return {
            value: String(opt.identifier ?? opt.value ?? ''),
            label: decodeHtmlEntities(String(opt.value ?? opt.identifier ?? '')),
            count,
          };
        })
        .filter((opt) => opt.value !== '');

      if (options.length === 0) return;

      const filterType = src.filterType || '';
      const key = src.nodeId != null ? `${filterType}:${src.nodeId}` : filterType;
      const requestKey = kind === 'node' ? 'node.id' : kind === 'store' ? 'catalogId' : filterType;

      filterGroups.push({
        key,
        name: decodeHtmlEntities(String(src.name || filterType)),
        requestKey,
        uiType: src.uiType || 'checkbox',
        count: typeof src.count === 'number' ? src.count : undefined,
        options,
        isBoolean: false,
      });
    };

    for (const group of raw) {
      const filterType = group?.filterType || '';
      if (!filterType) continue;

      // Sort is delivered as a pseudo-filter group — feed the sort drawer
      if (filterType === 'sort') {
        const opts = Array.isArray(group.sortFilterOptions) ? group.sortFilterOptions : [];

        if (opts.length > 0) {
          sortOptions = opts.map((opt) => ({
            identifier: String(opt.identifier ?? opt.value ?? ''),
            label: decodeHtmlEntities(String(opt.value ?? opt.identifier ?? '')),
          }));
        }
        continue;
      }

      // Department → a single group rendered as category headers (Health &
      // Nutrition, Beauty, …) each followed by its nested subcategory options.
      // `optionGroups` drives the grouped sub-drawer; `options` is the flattened
      // list kept for selection/count logic. All values are nodeIds sent under
      // `node.id`.
      if (Array.isArray(group.departmentOptions)) {
        const toOption = (opt) => ({
          value: String(opt.nodeId ?? ''),
          label: decodeHtmlEntities(String(opt.value ?? opt.nodeId ?? '')),
          count: typeof opt.count === 'number' ? opt.count : undefined,
        });

        // Build category groups, de-duplicating values across the whole
        // Department group. A repeated nodeId (a category whose id also appears
        // as a subcategory, or a subcategory shared by two departments) would
        // render two checkboxes sharing one value — selecting one would appear
        // to toggle the other. Keep only the first occurrence of each value.
        const seen = new Set();
        const optionGroups = [];
        for (const dept of group.departmentOptions) {
          const headValue = String(dept.nodeId ?? '');
          const value = headValue !== '' && !seen.has(headValue) ? headValue : '';
          if (value !== '') seen.add(value);

          const options = [];
          for (const raw of Array.isArray(dept.filterOptions) ? dept.filterOptions : []) {
            const opt = toOption(raw);
            if (opt.value === '' || seen.has(opt.value)) continue;
            seen.add(opt.value);
            options.push(opt);
          }

          if (value !== '' || options.length > 0) {
            optionGroups.push({
              name: decodeHtmlEntities(String(dept.name ?? '')),
              value,
              count: typeof dept.count === 'number' ? dept.count : undefined,
              options,
            });
          }
        }

        // Flattened list keeps both the selectable category (level1) and its
        // subcategories (level2) so selection/chip logic resolves every value.
        const options = optionGroups.flatMap((grp) => {
          const head = grp.value !== '' ? [{ value: grp.value, label: grp.name, count: grp.count }] : [];
          return head.concat(grp.options);
        });

        if (options.length > 0) {
          filterGroups.push({
            key: filterType,
            name: decodeHtmlEntities(String(group.name || filterType)),
            requestKey: 'node.id',
            uiType: group.uiType || 'radio',
            count: typeof group.count === 'number' ? group.count : undefined,
            options,
            optionGroups,
            isBoolean: false,
          });
        }
        continue;
      }

      if (Array.isArray(group.filterOptions)) {
        const isNode = group.filterOptions.some((opt) => opt?.nodeId != null && opt.nodeId !== '');
        pushOptionGroup(group, group.filterOptions, isNode ? 'node' : 'identifier');
        continue;
      }

      if (Array.isArray(group.storeFilterOptions)) {
        pushOptionGroup(group, group.storeFilterOptions, 'store');
        continue;
      }

      // No options + checkbox → boolean toggle (Sale, FSA/HSA, Shipping, etc.)
      if (group.uiType === 'checkbox') {
        filterGroups.push({
          key: filterType,
          name: decodeHtmlEntities(String(group.name || filterType)),
          requestKey: filterType,
          uiType: 'checkbox',
          count: typeof group.count === 'number' ? group.count : undefined,
          options: null,
          isBoolean: true,
        });
      }
    }

    return { filterGroups: this.#orderGroups(filterGroups), sortOptions };
  }

  /**
   * Re-orders filter groups into the merchant-specified sequence:
   * Exclusive Brands, FSA/HSA Eligible, Shipping Offers, Sale, Department,
   * Brand, Store, Price, then any remaining product-attribute filters.
   *
   * Ranks by both `filterType` and the (stable) display `name` so it survives
   * variation in the API's filterType strings. The sort is stable: groups
   * sharing a rank (the many department/category and attribute groups) keep
   * their original API order.
   *
   * @param {Array<NormalisedGroup>} groups
   * @returns {Array<NormalisedGroup>}
   */
  #orderGroups(groups) {
    const norm = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

    const rankOf = (group) => {
      // The filterType lives in the key prefix (`Category:123` → `category`);
      // requestKey is a request param (`node.id`) and not the filterType.
      const ft = norm(String(group.key ?? '').split(':')[0]);
      const nm = norm(group.name);

      if (ft.includes('exclusivebrands') || nm.includes('exclusive')) return 0;
      if (ft.includes('fsahsa') || nm.includes('fsa') || nm.includes('hsa')) return 1;
      if (ft.includes('shippingoffers') || nm.includes('shipping')) return 2;
      if (ft === 'isonsale' || ft.includes('saleitems') || nm === 'sale') return 3;
      if (ft === 'category' || ft === 'department' || nm.includes('department')) return 4;
      if (ft === 'brand' || nm === 'brand' || nm === 'brands') return 5;
      if (ft === 'stores' || ft === 'store' || nm.includes('store')) return 6;
      if (ft === 'price' || nm.includes('price')) return 7;
      return 8;
    };

    return groups
      .map((group, index) => ({ group, index, rank: rankOf(group) }))
      .sort((a, b) => a.rank - b.rank || a.index - b.index)
      .map((entry) => entry.group);
  }

  // ---------------------------------------------------------------------------
  // Render — toolbar (always reflects committed state)
  // ---------------------------------------------------------------------------

  /**
   * Main render entry point — updates toolbar sub-regions (trigger buttons,
   * result count, active chips). Drawer bodies are populated on open.
   *
   * @param {Array<NormalisedGroup>} filterGroups
   */
  #render(filterGroups) {
    const activeContainer = this.querySelector('[data-ngf-active-filters]');
    const resultCount = this.querySelector('[data-ngf-result-count]');
    const toolbar = this.querySelector('[data-ngf-toolbar]');
    const resultsRow = this.querySelector('[data-ngf-results-row]');
    const skeleton = this.querySelector('[data-ngf-skeleton]');

    if (skeleton) skeleton.hidden = true;
    if (toolbar) toolbar.hidden = false;

    // Accumulate group meta so a currently-selected group's meta survives a
    // later response that no longer returns that group.
    for (const group of filterGroups) {
      this.#groupMeta.set(group.key, group);
    }

    // Active chips (committed state)
    if (activeContainer) {
      activeContainer.innerHTML = this.#renderActiveChips();
    }

    // Result count — hidden at zero results, where the empty-state section
    // presents the query back to the customer instead.
    if (resultCount) {
      if (this.#showProductCount && this.#totalCount > 0) {
        const query = new URLSearchParams(window.location.search).get('q') || '';
        if (this.#context === 'slp' && query) {
          // Bold "{count} Results", light "for “query”". Derive the connector
          // ("for") by stripping the "Results" label from "Results for" so it
          // stays i18n-driven; fall back to the full label if it doesn't prefix.
          const connector = this.#i18n.resultsFor.startsWith(this.#i18n.results)
            ? this.#i18n.resultsFor.slice(this.#i18n.results.length).trim()
            : this.#i18n.resultsFor;
          resultCount.innerHTML = `${this.#totalCount} ${htmlEscape(this.#i18n.results)} <span class="tw:font-light">${htmlEscape(connector)} “${htmlEscape(query)}”</span>`;
        } else {
          resultCount.textContent = `${this.#totalCount} ${this.#i18n.results}`;
        }
        resultCount.hidden = false;
      } else {
        resultCount.hidden = true;
      }
    }

    // Show results row when there is a result count or active chips to display
    // (committed selections OR the fixed base-node pills on a PLP).
    if (resultsRow) {
      const hasBaseNodePills = this.#context === 'plp' && this.#baseNodeIds.length > 0;
      const hasChips = this.#selectedFilters.size > 0 || hasBaseNodePills;
      const hasCount = this.#showProductCount && this.#totalCount > 0;
      resultsRow.hidden = !(hasChips || hasCount);
    }

    // Filter / Sort pill labels are rendered (translated) in Liquid and are
    // static — no JS overwrite needed here.

    // Hide sort trigger if show_sort is disabled
    const sortTrigger = this.querySelector('[data-ngf-open-sort]');
    if (sortTrigger) {
      /** @type {HTMLElement} */ (sortTrigger).style.display = this.#showSort ? '' : 'none';
    }

    // Render dynamic group pills in the scrollable row
    this.#renderFilterPills();

    // Attach scroll listener + size observer once for the pills row arrows
    if (!this.#scrollListenerAttached) {
      const scroll = this.querySelector('[data-ngf-pills-scroll]');
      if (scroll) {
        scroll.addEventListener('scroll', () => this.#updateScrollArrows(), { passive: true });
        if ('ResizeObserver' in window) {
          this.#resizeObserver = new ResizeObserver(() => this.#updateScrollArrows());
          this.#resizeObserver.observe(scroll);
        }
        this.#scrollListenerAttached = true;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Render — dynamic toolbar pills + scroll indicator
  // ---------------------------------------------------------------------------

  /**
   * Populates the `[data-ngf-pills]` container with one pill button per filter
   * group (excluding sort). Active groups receive a black border.
   */
  #renderFilterPills() {
    const container = this.querySelector('[data-ngf-pills]');
    if (!container) return;

    container.innerHTML = this.#filterGroups
      .map((group) => {
        const vals = this.#selectedFilters.get(group.key);
        const isActive = vals != null && vals.size > 0;
        return `<button class="${PILL_CLS}${isActive ? ' ' + PILL_ACTIVE_CLS : ''} ${group.isBoolean ? 'fs-filters__option' : 'fs-filters__group-toggle'}" data-ngf-pill-group="${htmlEscape(group.key)}" data-ngf-pill-boolean="${group.isBoolean}" type="button">${htmlEscape(group.name)}</button>`;
      })
      .join('');

    // Defer to after layout/visibility: on the first hydration response this
    // render runs while the host is still [hidden] (revealed immediately
    // after), so a synchronous measure would read scrollWidth/clientWidth = 0.
    requestAnimationFrame(() => this.#updateScrollArrows());
  }

  /**
   * Shows or hides the left/right scroll-arrow controls based on whether the
   * group-pills row overflows and the current scroll position. The Filter and
   * Sort pills live outside this scroll container, so they never move — only
   * the dynamic group pills scroll.
   */
  #updateScrollArrows() {
    const scroll = this.querySelector('[data-ngf-pills-scroll]');
    const leftArrow = this.querySelector('[data-ngf-scroll-left]');
    const rightArrow = this.querySelector('[data-ngf-scroll-right]');
    if (!scroll) return;

    const hasOverflow = scroll.scrollWidth > scroll.clientWidth + 1;
    const atStart = scroll.scrollLeft <= 1;
    const atEnd = scroll.scrollLeft + scroll.clientWidth >= scroll.scrollWidth - 1;

    if (leftArrow) /** @type {HTMLElement} */ (leftArrow).hidden = !hasOverflow || atStart;
    if (rightArrow) /** @type {HTMLElement} */ (rightArrow).hidden = !hasOverflow || atEnd;
  }

  /**
   * Scrolls the group-pills row by a single pill in the given direction (snaps
   * to the next/previous pill's leading edge rather than jumping a whole page).
   *
   * @param {number} direction - -1 to scroll left, 1 to scroll right.
   */
  #scrollPills(direction) {
    const scroll = this.querySelector('[data-ngf-pills-scroll]');
    if (!scroll) return;

    const pills = Array.from(scroll.querySelectorAll('[data-ngf-pill-group]'));
    if (pills.length === 0) {
      scroll.scrollBy({ left: direction * scroll.clientWidth * 0.7, behavior: 'smooth' });
      return;
    }

    // Position of each pill's leading edge within the scroll content.
    const scrollLeft = scroll.scrollLeft;
    const base = scroll.getBoundingClientRect().left;
    const posOf = (el) => el.getBoundingClientRect().left - base + scrollLeft;
    const EPS = 2;

    let target;
    if (direction > 0) {
      const next = pills.find((p) => posOf(p) > scrollLeft + EPS);
      target = next ? posOf(next) : scroll.scrollWidth;
    } else {
      const prior = pills.filter((p) => posOf(p) < scrollLeft - EPS);
      target = prior.length ? posOf(prior[prior.length - 1]) : 0;
    }

    scroll.scrollTo({ left: target, behavior: 'smooth' });
  }

  // ---------------------------------------------------------------------------
  // Mobile grid / list view toggle
  // ---------------------------------------------------------------------------

  /** @returns {'grid' | 'list'} Persisted view preference, defaulting to grid. */
  #storedView() {
    try {
      return localStorage.getItem('ng:product-view') === 'list' ? 'list' : 'grid';
    } catch (error) {
      return 'grid';
    }
  }

  /**
   * Reflects the persisted view on the toggle buttons on connect. Does NOT
   * dispatch — the products section reads the same localStorage key on connect,
   * so the initial state is already consistent.
   */
  #initViewToggle() {
    this.#reflectView(this.#storedView());
  }

  /** Marks the matching toggle button as pressed. @param {'grid'|'list'} view */
  #reflectView(view) {
    for (const btn of this.querySelectorAll('[data-ngf-view]')) {
      btn.setAttribute('aria-pressed', String(btn.getAttribute('data-ngf-view') === view));
    }
  }

  /**
   * Commits a view change from a toggle click: updates button state, persists
   * the choice, and notifies the products section via a `theme:view:change`
   * event (grid = 2 columns, list = 1 column on mobile).
   *
   * @param {'grid' | 'list'} view
   */
  #setView(view) {
    this.#reflectView(view);
    try {
      localStorage.setItem('ng:product-view', view);
    } catch (error) {
      /* localStorage unavailable (private mode) — change still applies for the session */
    }
    document.dispatchEvent(new CustomEvent('theme:view:change', { detail: { view } }));
  }

  // ---------------------------------------------------------------------------
  // Render — sort drawer body
  // ---------------------------------------------------------------------------

  /** Populates the sort drawer body with radio options from staged state. */
  #renderSortDrawerBody() {
    const body = this.#q('[data-ngf-sort-body]');
    if (!body) return;

    body.innerHTML = this.#sortOptions
      .map((opt) => {
        const checked = opt.identifier === this.#stagedSort ? ' checked' : '';
        return `<label class="${ROW_CLS} fs-sorting__option">
          <span>${htmlEscape(opt.label)}</span>
          <input type="radio" name="ngf-sort" value="${htmlEscape(opt.identifier)}"${checked} data-ngf-staged-sort class="${INPUT_CLS}">
          <span class="${RADIO_CLS}"></span>
        </label>`;
      })
      .join('');
  }

  // ---------------------------------------------------------------------------
  // Render — filter drawer body
  // ---------------------------------------------------------------------------

  /** Populates the filter drawer body with the top-level group list. */
  #renderFilterDrawerBody() {
    const body = this.#q('[data-ngf-filter-body]');
    if (!body) return;

    body.innerHTML = this.#filterGroups
      .map((group) => this.#renderDrawerFilterGroup(group))
      .join('');
  }

  /**
   * Builds HTML for a single filter group row in the top-level filter drawer.
   *
   * - Boolean toggles render as a row with a custom checkbox indicator.
   * - Option groups render as a navigation row with a right chevron that opens
   *   a sub-drawer (no inline accordion).
   *
   * @param {NormalisedGroup} group
   * @returns {string}
   */
  #renderDrawerFilterGroup(group) {
    const staged = this.#stagedFilters.get(group.key);

    // Boolean toggle — the group itself is the filter (e.g. Sale, FSA/HSA)
    if (group.isBoolean) {
      const isChecked = staged?.has('true') ? ' checked' : '';

      return `<label class="${ROW_CLS} fs-filters__option">
        <span>${htmlEscape(group.name)}</span>
        <input type="checkbox" data-ngf-staged-filter="${htmlEscape(group.key)}" data-ngf-boolean data-ngf-label="${htmlEscape(group.name)}" value="true"${isChecked} class="${INPUT_CLS}">
        <span class="${BOX_CLS}"></span>
      </label>`;
    }

    // Option group — navigation row opening a sub-drawer
    return `<button class="${GROUP_BTN_CLS} fs-filters__group-toggle" type="button" data-ngf-open-group="${htmlEscape(group.key)}">
      <span class="tw:flex-1">${htmlEscape(group.name)}</span>
      <svg class="tw:h-4 tw:w-4 tw:shrink-0 tw:lg:h-6 tw:lg:w-6" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </button>`;
  }

  // ---------------------------------------------------------------------------
  // Price range slider helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns `true` when the given group is a price filter.
   * Price groups have `requestKey` equal to the raw `filterType` ('price') and
   * their options carry identifier-format values like '0-24.99'.
   *
   * @param {NormalisedGroup | undefined | null} group
   * @returns {boolean}
   */
  #isPriceGroup(group) {
    if (!group) return false;
    const ft = String(group.key ?? '').toLowerCase();
    const nm = String(group.name ?? '').toLowerCase();
    return ft === 'price' || nm.includes('price');
  }

  /**
   * Extracts the overall min/max bounds from the price group's predefined
   * options. Each option identifier is in the format 'low-high' (e.g.
   * '0-24.99', '25-49.99', '200+'). The '+' suffix means open-ended upper
   * bound — we use a fallback ceiling of 500 for slider purposes.
   *
   * @param {NormalisedGroup} group
   * @returns {{ min: number, max: number }}
   */
  #getPriceBounds(group) {
    let globalMin = Infinity;
    let globalMax = -Infinity;

    for (const opt of group.options || []) {
      const parts = String(opt.value).split('-');
      const lo = parseFloat(parts[0]);
      if (!Number.isNaN(lo)) globalMin = Math.min(globalMin, lo);

      if (parts.length > 1) {
        // Handle '200+' → open-ended upper bound
        const hiStr = parts[parts.length - 1].replace('+', '');
        const hi = parseFloat(hiStr);
        if (!Number.isNaN(hi)) globalMax = Math.max(globalMax, hi);
      } else {
        // Single value with '+' (e.g. '200+') — treat as open-ended
        const singleStr = String(opt.value).replace('+', '');
        const single = parseFloat(singleStr);
        if (!Number.isNaN(single)) {
          globalMin = Math.min(globalMin, single);
          globalMax = Math.max(globalMax, single);
        }
      }
    }

    // Fallback when the highest bucket is open-ended (e.g. '200+')
    if (globalMax <= globalMin) globalMax = globalMin + 500;
    if (!Number.isFinite(globalMin)) globalMin = 0;
    if (!Number.isFinite(globalMax)) globalMax = 500;

    return { min: globalMin, max: globalMax };
  }

  /**
   * Finds the predefined price radio option whose numeric bounds match the
   * given range, so a slider/input selection reuses the radio's identifier and
   * label (one unified pill — no separate "range meter" chip). Open-ended
   * buckets ('200+') match when their lower bound equals `lo` and `hi` reaches
   * the slider ceiling. Comparison is rounded to whole units to tolerate the
   * API's decimal identifiers ('0-76.0' vs the integer slider value 0-76).
   *
   * @param {NormalisedGroup} group
   * @param {number} lo
   * @param {number} hi
   * @returns {{ value: string, label: string } | null}
   */
  #matchPriceOption(group, lo, hi) {
    const { max: absMax } = this.#getPriceBounds(group);
    for (const opt of group.options || []) {
      const raw = String(opt.value);
      const openEnded = raw.includes('+') || /-\s*$/.test(raw);
      const parts = raw.replace('+', '').split('-').filter((s) => s !== '');
      const oLo = parseFloat(parts[0]);
      if (Number.isNaN(oLo)) continue;
      let oHi = parts.length > 1 ? parseFloat(parts[parts.length - 1]) : oLo;
      if (openEnded) oHi = absMax;
      if (Number.isNaN(oHi)) continue;
      if (Math.round(oLo) === Math.round(lo) && Math.round(oHi) === Math.round(hi)) {
        return { value: opt.value, label: opt.label };
      }
    }
    return null;
  }

  /**
   * Renders the price range controls HTML (dual slider + min/max inputs).
   * Appended below the predefined radio options in the sub-drawer body.
   *
   * @param {NormalisedGroup} group
   * @returns {string}
   */
  #renderPriceRangeControls(group) {
    const { min: absMin, max: absMax } = this.#getPriceBounds(group);
    const staged = this.#stagedFilters.get(group.key);

    // Determine current slider values: if there is a staged selection, parse it
    let curMin = absMin;
    let curMax = absMax;

    if (staged && staged.size > 0) {
      // Get the first (and typically only) staged value — it's an identifier like '25-49.99'
      const identifier = staged.keys().next().value;
      const parts = String(identifier).split('-');
      const lo = parseFloat(parts[0]);
      if (!Number.isNaN(lo)) curMin = lo;
      if (parts.length > 1) {
        const hi = parseFloat(parts[parts.length - 1].replace('+', ''));
        if (!Number.isNaN(hi)) curMax = hi;
      }
    }

    const sym = htmlEscape(this.#currencySymbol);

    return `<div class="tw:px-5 tw:lg:px-7 tw:pt-5 tw:pb-6 tw:border-b tw:border-b-[rgb(var(--color-foreground-rgb)/0.20)]" data-ngf-price-controls data-ngf-price-group="${htmlEscape(group.key)}">
      <div class="tw:flex tw:items-center tw:justify-between tw:mb-4">
        <span class="tw:text-[14px] tw:font-medium tw:leading-[1.2]">${htmlEscape(this.#i18n.priceCustomRange)}</span>
      </div>
      <div data-ngf-price-slider>
        <div data-ngf-price-track>
          <div data-ngf-price-fill></div>
        </div>
        <input type="range" min="${absMin}" max="${absMax}" value="${curMin}" step="1" data-ngf-price-range-min aria-label="${htmlEscape(this.#i18n.priceMin)}">
        <input type="range" min="${absMin}" max="${absMax}" value="${curMax}" step="1" data-ngf-price-range-max aria-label="${htmlEscape(this.#i18n.priceMax)}">
      </div>
      <div class="tw:flex tw:items-center tw:gap-3 tw:mt-4">
        <label class="tw:flex tw:flex-1 tw:items-center tw:gap-1.5 tw:rounded-[8px] tw:border tw:border-[#D8D7D8] tw:px-3 tw:py-2">
          <span class="tw:text-[13px] tw:text-[#888] tw:leading-[1]">${sym}</span>
          <input type="number" min="${absMin}" max="${absMax}" value="${curMin}" step="1" data-ngf-price-input-min class="tw:w-full tw:border-0 tw:bg-transparent tw:text-[14px] tw:leading-[1.2] tw:outline-none tw:p-0 tw:font-[inherit] tw:text-[var(--color-foreground)]" placeholder="${htmlEscape(this.#i18n.priceMin)}" aria-label="${htmlEscape(this.#i18n.priceMin)}">
        </label>
        <span class="tw:text-[14px] tw:text-[#888]">–</span>
        <label class="tw:flex tw:flex-1 tw:items-center tw:gap-1.5 tw:rounded-[8px] tw:border tw:border-[#D8D7D8] tw:px-3 tw:py-2">
          <span class="tw:text-[13px] tw:text-[#888] tw:leading-[1]">${sym}</span>
          <input type="number" min="${absMin}" max="${absMax}" value="${curMax}" step="1" data-ngf-price-input-max class="tw:w-full tw:border-0 tw:bg-transparent tw:text-[14px] tw:leading-[1.2] tw:outline-none tw:p-0 tw:font-[inherit] tw:text-[var(--color-foreground)]" placeholder="${htmlEscape(this.#i18n.priceMax)}" aria-label="${htmlEscape(this.#i18n.priceMax)}">
        </label>
      </div>
    </div>`;
  }

  /**
   * Synchronises the price slider thumbs, track fill, and min/max inputs so
   * they all reflect the same values. Called after any slider or input change.
   *
   * @param {HTMLElement} container - The `[data-ngf-price-controls]` wrapper.
   * @param {number} minVal
   * @param {number} maxVal
   */
  #syncPriceUI(container, minVal, maxVal) {
    const rangeMin = /** @type {HTMLInputElement | null} */ (container.querySelector('[data-ngf-price-range-min]'));
    const rangeMax = /** @type {HTMLInputElement | null} */ (container.querySelector('[data-ngf-price-range-max]'));
    const inputMin = /** @type {HTMLInputElement | null} */ (container.querySelector('[data-ngf-price-input-min]'));
    const inputMax = /** @type {HTMLInputElement | null} */ (container.querySelector('[data-ngf-price-input-max]'));
    const fill = /** @type {HTMLElement | null} */ (container.querySelector('[data-ngf-price-fill]'));

    if (rangeMin) rangeMin.value = String(minVal);
    if (rangeMax) rangeMax.value = String(maxVal);
    if (inputMin) inputMin.value = String(minVal);
    if (inputMax) inputMax.value = String(maxVal);

    // Update the track fill bar position
    if (fill && rangeMin) {
      const absMin = parseFloat(rangeMin.min);
      const absMax = parseFloat(rangeMin.max);
      const range = absMax - absMin || 1;
      const left = ((minVal - absMin) / range) * 100;
      const right = ((maxVal - absMin) / range) * 100;
      fill.style.left = `${left}%`;
      fill.style.width = `${right - left}%`;
    }
  }

  /**
   * Handles real-time `input` events on the dual range slider thumbs. Updates
   * the visual fill and keeps the two thumbs from crossing.
   *
   * @param {Event} event
   */
  #handlePriceSliderInput(event) {
    const target = /** @type {HTMLInputElement} */ (event.target);
    if (!target.matches('[data-ngf-price-range-min], [data-ngf-price-range-max]')) return;

    const container = target.closest('[data-ngf-price-controls]');
    if (!container) return;

    const rangeMin = /** @type {HTMLInputElement} */ (container.querySelector('[data-ngf-price-range-min]'));
    const rangeMax = /** @type {HTMLInputElement} */ (container.querySelector('[data-ngf-price-range-max]'));
    if (!rangeMin || !rangeMax) return;

    let lo = parseInt(rangeMin.value, 10);
    let hi = parseInt(rangeMax.value, 10);

    // Prevent crossover
    if (target === rangeMin && lo > hi) {
      lo = hi;
      rangeMin.value = String(lo);
    } else if (target === rangeMax && hi < lo) {
      hi = lo;
      rangeMax.value = String(hi);
    }

    this.#syncPriceUI(/** @type {HTMLElement} */ (container), lo, hi);
  }

  /**
   * Stages a price range from the slider/input controls WITHOUT applying it —
   * the price sub-drawer defers application until "Done" is clicked. When the
   * range matches a predefined radio bucket, the radio's identifier + label are
   * staged (and that radio is checked) so applying produces the same single
   * pill as picking the radio directly. A range spanning the full bounds with
   * no matching bucket is treated as "no price filter" and clears the staged
   * entry. Otherwise a synthetic 'low-high' identifier is staged and all radios
   * are unchecked.
   *
   * @param {HTMLElement} container - The `[data-ngf-price-controls]` wrapper.
   */
  #stagePriceRange(container) {
    const groupKey = container.getAttribute('data-ngf-price-group') || '';
    const inputMin = /** @type {HTMLInputElement | null} */ (container.querySelector('[data-ngf-price-input-min]'));
    const inputMax = /** @type {HTMLInputElement | null} */ (container.querySelector('[data-ngf-price-input-max]'));
    if (!inputMin || !inputMax || !groupKey) return;

    const lo = parseInt(inputMin.value, 10);
    const hi = parseInt(inputMax.value, 10);
    if (Number.isNaN(lo) || Number.isNaN(hi) || lo > hi) return;

    const group = this.#groupMeta.get(groupKey) || this.#filterGroups.find((g) => g.key === groupKey);
    const match = group ? this.#matchPriceOption(group, lo, hi) : null;

    // A full-span range that matches no bucket means "any price" → no filter.
    const absMin = parseInt(inputMin.min, 10);
    const absMax = parseInt(inputMin.max, 10);
    const isFullRange = !match
      && (Number.isNaN(absMin) || lo <= absMin)
      && (Number.isNaN(absMax) || hi >= absMax);

    if (isFullRange) {
      this.#stagedFilters.delete(groupKey);
    } else {
      const identifier = match ? match.value : `${lo}-${hi}`;
      const label = match ? match.label : `${this.#currencySymbol}${lo} – ${this.#currencySymbol}${hi}`;

      // Single staged entry for the price group (clear any prior radio/custom).
      if (!this.#stagedFilters.has(groupKey)) {
        this.#stagedFilters.set(groupKey, new Map());
      }
      const staged = this.#stagedFilters.get(groupKey);
      staged.clear();
      staged.set(identifier, label);
    }

    // Reflect the match in the radio list: check the matching bucket, else
    // uncheck every predefined radio option.
    const subDrawer = this.#q('[data-ngf-sub-drawer]');
    if (subDrawer) {
      for (const input of subDrawer.querySelectorAll(`[data-ngf-staged-filter="${CSS.escape(groupKey)}"]`)) {
        /** @type {HTMLInputElement} */ (input).checked = !!match && input.getAttribute('value') === match.value;
      }
    }
  }

  /**
   * Swaps the sub-drawer footer between the default "Clear" button and the
   * price-specific "Reset" + "Done" buttons. Price selections are staged and
   * applied on "Done"; every other group keeps the live-commit "Clear" footer.
   *
   * @param {NormalisedGroup} group
   */
  #updateSubDrawerFooter(group) {
    const isPrice = this.#isPriceGroup(group);
    const clearBtn = this.#q('[data-ngf-sub-drawer] [data-ngf-clear-filter]');
    const resetBtn = this.#q('[data-ngf-price-reset]');
    const doneBtn = this.#q('[data-ngf-price-done]');
    if (clearBtn) clearBtn.hidden = isPrice;
    if (resetBtn) resetBtn.hidden = !isPrice;
    if (doneBtn) doneBtn.hidden = !isPrice;
  }

  /**
   * Populates the sub-drawer body with the option checkboxes for the currently
   * active group (staged state).
   */
  #renderSubDrawerBody() {
    const body = this.#q('[data-ngf-sub-body]');
    if (!body || !this.#activeGroupKey) return;

    const group = this.#filterGroups.find((g) => g.key === this.#activeGroupKey)
      || this.#groupMeta.get(this.#activeGroupKey);
    if (!group) {
      body.innerHTML = '';
      return;
    }

    this.#updateSubDrawerFooter(group);

    const staged = this.#stagedFilters.get(group.key);

    const optionRow = (opt, rowCls, withCount) => {
      const isChecked = staged?.has(opt.value) ? ' checked' : '';
      const label = withCount && opt.count != null ? `${opt.label} (${opt.count})` : opt.label;

      return `<label class="${rowCls} fs-filters__option">
        <span class="tw:flex-1">${htmlEscape(label)}</span>
        <input type="checkbox" data-ngf-staged-filter="${htmlEscape(group.key)}" data-ngf-ui="${htmlEscape(group.uiType)}" data-ngf-label="${htmlEscape(opt.label)}" value="${htmlEscape(opt.value)}"${isChecked} class="${INPUT_CLS}">
        <span class="${BOX_CLS}"></span>
      </label>`;
    };

    // Grouped sub-drawer (Department): selectable category header + indented
    // subcategory options, all showing counts.
    if (Array.isArray(group.optionGroups) && group.optionGroups.length > 0) {
      body.innerHTML = group.optionGroups
        .map((grp) => {
          const header = grp.value
            ? optionRow({ value: grp.value, label: grp.name, count: grp.count }, SUBGROUP_HEADER_ROW_CLS, true)
            : `<div class="${SUBGROUP_HEADER_CLS}">${htmlEscape(grp.name)}</div>`;
          const rows = grp.options.map((opt) => optionRow(opt, SUBGROUP_OPTION_CLS, true)).join('');
          return header + rows;
        })
        .join('');
      return;
    }

    body.innerHTML = (group.options || []).map((opt) => optionRow(opt, ROW_CLS, true)).join('');

    // For price groups, append the dual-handle slider + min/max inputs below
    // the predefined radio options.
    if (this.#isPriceGroup(group)) {
      body.insertAdjacentHTML('beforeend', this.#renderPriceRangeControls(group));
      // Initialise the track fill after the DOM is in place
      const priceControls = body.querySelector('[data-ngf-price-controls]');
      if (priceControls) {
        const rangeMin = /** @type {HTMLInputElement | null} */ (priceControls.querySelector('[data-ngf-price-range-min]'));
        const rangeMax = /** @type {HTMLInputElement | null} */ (priceControls.querySelector('[data-ngf-price-range-max]'));
        if (rangeMin && rangeMax) {
          this.#syncPriceUI(
            /** @type {HTMLElement} */ (priceControls),
            parseInt(rangeMin.value, 10),
            parseInt(rangeMax.value, 10)
          );
        }
      }
    }
  }

  /** @returns {string} Active filter chip buttons + "clear filters" button. */
  #renderActiveChips() {
    const chips = [];
    const closeSvg = '<svg width="7" height="7" viewBox="0 0 7 7" fill="none" aria-hidden="true"><path d="M1 1L6 6M6 1L1 6" stroke="#000000" stroke-width="1.5" stroke-linecap="round"/></svg>';

    // Fixed (non-removable) pills for the collection's base node IDs (PLP).
    // These define the collection scope and have no close control, so the
    // shopper cannot clear them.
    if (this.#context === 'plp') {
      for (const nodeId of this.#baseNodeIds) {
        const label = this.#baseNodeLabels.get(nodeId) || nodeId;
        chips.push(
          `<span class="${FIXED_CHIP_CLS}" data-ngf-base-node="${htmlEscape(nodeId)}">${htmlEscape(label)}</span>`
        );
      }
    }

    // Removable chips for the shopper's committed filter selections.
    let hasRemovable = false;
    for (const [key, values] of this.#selectedFilters) {
      for (const [value, label] of values) {
        hasRemovable = true;
        chips.push(
          `<button class="${CHIP_CLS} fs-filters__remove" data-ngf-remove="${htmlEscape(key)}" data-ngf-value="${htmlEscape(value)}" type="button">${htmlEscape(label)} ${closeSvg}</button>`
        );
      }
    }

    // "clear filters" only clears removable selections — hide it when the only
    // chips are the fixed base-node pills.
    if (hasRemovable) {
      chips.push(
        `<button class="${CLEAR_ALL_CLS} fs-filters__clear" data-ngf-clear type="button">${htmlEscape(this.#i18n.clearFilters)}</button>`
      );
    }

    return chips.join('');
  }

  // ---------------------------------------------------------------------------
  // Drawer management
  // ---------------------------------------------------------------------------

  #openSortDrawer() {
    if (this.#activeDrawer) this.#closeActiveDrawer();

    // Snapshot committed state into staged
    this.#stagedSort = this.#currentSort;

    const drawer = this.#q('[data-ngf-sort-drawer]');
    const overlay = this.#q('[data-ngf-overlay]');

    this.#renderSortDrawerBody();

    if (drawer) {
      drawer.setAttribute('data-open', '');
      drawer.setAttribute('aria-hidden', 'false');
    }
    if (overlay) overlay.setAttribute('data-open', '');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('ngf-drawer-open');
    this.#activeDrawer = 'sort';

    // Move focus into the drawer
    const firstInput = drawer?.querySelector('input[type="radio"]');
    if (firstInput) /** @type {HTMLElement} */ (firstInput).focus();
  }

  #closeSortDrawer() {
    const drawer = this.#q('[data-ngf-sort-drawer]');
    const overlay = this.#q('[data-ngf-overlay]');

    if (drawer) {
      drawer.removeAttribute('data-open');
      drawer.setAttribute('aria-hidden', 'true');
    }
    if (overlay) overlay.removeAttribute('data-open');
    document.body.style.overflow = '';
    document.body.classList.remove('ngf-drawer-open');
    this.#activeDrawer = null;

    // Discard staged state (don't commit)
    this.#stagedSort = this.#currentSort;

    // Return focus to trigger
    if (this.#lastTrigger) {
      this.#lastTrigger.focus();
      this.#lastTrigger = null;
    }
  }

  #openFilterDrawer() {
    if (this.#activeDrawer) this.#closeActiveDrawer();

    // Snapshot committed state into staged
    this.#stagedFilters = cloneFilterMap(this.#selectedFilters);

    const drawer = this.#q('[data-ngf-filter-drawer]');
    const overlay = this.#q('[data-ngf-overlay]');

    this.#renderFilterDrawerBody();

    if (drawer) {
      drawer.setAttribute('data-open', '');
      drawer.setAttribute('aria-hidden', 'false');
    }
    if (overlay) overlay.setAttribute('data-open', '');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('ngf-drawer-open');
    this.#activeDrawer = 'filter';

    // Move focus into the drawer
    const closeBtn = drawer?.querySelector('[data-ngf-close-filter]');
    if (closeBtn) /** @type {HTMLElement} */ (closeBtn).focus();
  }

  #closeFilterDrawer() {
    // Tear down the sub-drawer if it is open
    if (this.#subOpen) this.#closeSubDrawer({ restoreFocus: false });

    const drawer = this.#q('[data-ngf-filter-drawer]');
    const overlay = this.#q('[data-ngf-overlay]');

    if (drawer) {
      drawer.removeAttribute('data-open');
      drawer.setAttribute('aria-hidden', 'true');
    }
    if (overlay) overlay.removeAttribute('data-open');
    document.body.style.overflow = '';
    document.body.classList.remove('ngf-drawer-open');
    this.#activeDrawer = null;

    // Discard staged state (don't commit)
    this.#stagedFilters = cloneFilterMap(this.#selectedFilters);

    // Return focus to trigger
    if (this.#lastTrigger) {
      this.#lastTrigger.focus();
      this.#lastTrigger = null;
    }
  }

  /**
   * Opens the filter sub-drawer showing a single option group's choices.
   * Slides over the filter drawer from the inline-end edge.
   *
   * @param {string} key - group key to display
   */
  #openSubDrawer(key) {
    const group = this.#filterGroups.find((g) => g.key === key) || this.#groupMeta.get(key);
    if (!group || !group.options) return;

    this.#activeGroupKey = key;
    this.#renderSubDrawerBody();

    const subDrawer = this.#q('[data-ngf-sub-drawer]');
    const title = this.#q('[data-ngf-sub-title]');

    if (title) title.textContent = group.name;
    if (subDrawer) {
      subDrawer.setAttribute('data-open', '');
      subDrawer.setAttribute('aria-hidden', 'false');
      subDrawer.setAttribute('aria-label', group.name);
    }
    this.#subOpen = true;

    const backBtn = subDrawer?.querySelector('[data-ngf-sub-back]');
    if (backBtn) /** @type {HTMLElement} */ (backBtn).focus();
  }

  /**
   * Closes the sub-drawer. When opened via a toolbar group pill
   * (`#pillDirectOpen`), closes everything (sub-drawer + overlay) and returns
   * to the toolbar. Otherwise navigates back to the filter drawer group list.
   *
   * @param {{ restoreFocus?: boolean }} [opts]
   */
  #closeSubDrawer(opts = {}) {
    const { restoreFocus = true } = opts;
    const subDrawer = this.#q('[data-ngf-sub-drawer]');

    if (subDrawer) {
      subDrawer.removeAttribute('data-open');
      subDrawer.setAttribute('aria-hidden', 'true');
    }

    // Pill-direct-open mode — close everything, skip filter drawer navigation
    if (this.#pillDirectOpen) {
      const overlay = this.#q('[data-ngf-overlay]');
      if (overlay) overlay.removeAttribute('data-open');
      document.body.style.overflow = '';
      document.body.classList.remove('ngf-drawer-open');
      this.#activeDrawer = null;
      this.#subOpen = false;
      this.#activeGroupKey = null;
      this.#pillDirectOpen = false;

      // Discard staged state (don't commit)
      this.#stagedFilters = cloneFilterMap(this.#selectedFilters);

      if (restoreFocus && this.#lastTrigger) {
        this.#lastTrigger.focus();
        this.#lastTrigger = null;
      }
      return;
    }

    // Normal mode — navigate back to the filter drawer group list
    const returnKey = this.#activeGroupKey;
    this.#subOpen = false;
    this.#activeGroupKey = null;

    // Refresh the group list so the count badge is up to date
    this.#renderFilterDrawerBody();

    // Return focus to the group row that opened the sub-drawer
    if (restoreFocus && returnKey) {
      const row = this.#q(`[data-ngf-open-group="${CSS.escape(returnKey)}"]`);
      if (row) /** @type {HTMLElement} */ (row).focus();
    }
  }

  #closeActiveDrawer() {
    if (this.#activeDrawer === 'sort') {
      this.#closeSortDrawer();
    } else if (this.#activeDrawer === 'filter') {
      this.#closeFilterDrawer();
    }
  }

  // ---------------------------------------------------------------------------
  // Pill-based toolbar interactions
  // ---------------------------------------------------------------------------

  /**
   * Immediately toggles a boolean filter on/off from a toolbar pill click.
   * Boolean groups (Sale, FSA/HSA, Exclusive Brands, Shipping Offers) have no
   * options — the group itself is the filter value.
   *
   * @param {string} key - group key
   */
  #toggleBooleanFilter(key) {
    const group = this.#filterGroups.find((g) => g.key === key) || this.#groupMeta.get(key);
    if (!group) return;

    if (this.#selectedFilters.has(key)) {
      this.#selectedFilters.delete(key);
    } else {
      this.#selectedFilters.set(key, new Map([['true', group.name]]));
    }

    // Keep staged state aligned
    this.#stagedFilters = cloneFilterMap(this.#selectedFilters);
    this.#render(this.#filterGroups);
    this.#currentPage = 1;
    this.#refetch();
  }

  /**
   * Opens the sub-drawer directly from a toolbar group pill, bypassing the
   * full filter drawer. Sets the `#pillDirectOpen` flag so close/apply/clear
   * operate in standalone mode.
   *
   * @param {string} key - group key
   */
  #openSubDrawerFromPill(key) {
    if (this.#activeDrawer) this.#closeActiveDrawer();

    const group = this.#filterGroups.find((g) => g.key === key) || this.#groupMeta.get(key);
    if (!group || !group.options) return;

    this.#pillDirectOpen = true;

    // Snapshot committed state into staged
    this.#stagedFilters = cloneFilterMap(this.#selectedFilters);

    this.#activeGroupKey = key;
    this.#renderSubDrawerBody();

    const subDrawer = this.#q('[data-ngf-sub-drawer]');
    const overlay = this.#q('[data-ngf-overlay]');
    const title = this.#q('[data-ngf-sub-title]');

    if (title) title.textContent = group.name;
    if (subDrawer) {
      subDrawer.setAttribute('data-open', '');
      subDrawer.setAttribute('aria-hidden', 'false');
      subDrawer.setAttribute('aria-label', group.name);
    }
    if (overlay) overlay.setAttribute('data-open', '');

    document.body.style.overflow = 'hidden';
    document.body.classList.add('ngf-drawer-open');
    this.#activeDrawer = 'filter';
    this.#subOpen = true;

    const backBtn = subDrawer?.querySelector('[data-ngf-sub-back]');
    if (backBtn) /** @type {HTMLElement} */ (backBtn).focus();
  }

  // ---------------------------------------------------------------------------
  // Commit + Clear
  // ---------------------------------------------------------------------------

  /** Commits the staged sort to current state and refetches. Drawer stays open. */
  #commitSort() {
    this.#currentSort = this.#stagedSort;
    this.#render(this.#filterGroups);
    this.#currentPage = 1;
    this.#refetch();
  }

  /** Clears the sort and applies it immediately. */
  #clearSort() {
    this.#stagedSort = DEFAULT_SORT_VALUE;
    this.#currentSort = DEFAULT_SORT_VALUE;
    this.#renderSortDrawerBody();
    this.#render(this.#filterGroups);
    this.#currentPage = 1;
    this.#refetch();
  }

  /** Commits staged filters to current state and refetches. Drawer stays open. */
  #commitFilters() {
    this.#selectedFilters = cloneFilterMap(this.#stagedFilters);
    this.#render(this.#filterGroups);
    this.#currentPage = 1;
    this.#refetch();
  }

  /**
   * Clears ALL filters and applies the change immediately. Shared by the filter
   * drawer and sub-drawer Clear buttons so the behaviour is identical in both —
   * no Apply click needed.
   */
  #clearFilters() {
    this.#stagedFilters = new Map();
    this.#selectedFilters = new Map();
    // Close the drawer (and any open sub-drawer) once filters are cleared so the
    // shopper is returned to the results instead of staying in an empty drawer.
    this.#closeActiveDrawer();
    this.#render(this.#filterGroups);
    this.#currentPage = 1;
    this.#refetch();
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  /**
   * Handles change events on staged sort radios and staged filter checkboxes.
   *
   * @param {Event} event
   */
  #handleChange(event) {
    const target = /** @type {HTMLElement} */ (event.target);

    // Staged sort radio change — commit immediately (no Apply click needed),
    // then close the drawer (MA-1419: with nothing left to apply, the open
    // drawer only hides the re-sorted results).
    if (target.matches('[data-ngf-staged-sort]')) {
      this.#stagedSort = /** @type {HTMLInputElement} */ (target).value;
      this.#commitSort();
      this.#closeActiveDrawer();
      return;
    }

    // Price min/max input change — stage the range (applied on "Done")
    if (target.matches('[data-ngf-price-input-min], [data-ngf-price-input-max]')) {
      const container = target.closest('[data-ngf-price-controls]');
      if (!container) return;

      const inputMin = /** @type {HTMLInputElement} */ (container.querySelector('[data-ngf-price-input-min]'));
      const inputMax = /** @type {HTMLInputElement} */ (container.querySelector('[data-ngf-price-input-max]'));
      if (!inputMin || !inputMax) return;

      let lo = parseInt(inputMin.value, 10);
      let hi = parseInt(inputMax.value, 10);
      const absMin = parseInt(inputMin.min, 10);
      const absMax = parseInt(inputMin.max, 10);

      // Clamp to bounds
      if (!Number.isNaN(lo)) lo = Math.max(absMin, Math.min(lo, absMax));
      if (!Number.isNaN(hi)) hi = Math.max(absMin, Math.min(hi, absMax));

      // Prevent crossover
      if (!Number.isNaN(lo) && !Number.isNaN(hi)) {
        if (lo > hi) {
          if (target === inputMin) lo = hi;
          else hi = lo;
        }
        this.#syncPriceUI(/** @type {HTMLElement} */ (container), lo, hi);
        this.#stagePriceRange(/** @type {HTMLElement} */ (container));
      }
      return;
    }

    // Price range slider change (mouse-up / touch-end) — stage the range
    if (target.matches('[data-ngf-price-range-min], [data-ngf-price-range-max]')) {
      const container = target.closest('[data-ngf-price-controls]');
      if (container) {
        this.#stagePriceRange(/** @type {HTMLElement} */ (container));
      }
      return;
    }

    // Staged filter toggle (checkbox / boolean / radio-behaviour group)
    if (target.matches('[data-ngf-staged-filter]')) {
      const input = /** @type {HTMLInputElement} */ (target);
      const key = input.getAttribute('data-ngf-staged-filter') || '';
      const label = input.getAttribute('data-ngf-label') || input.value;
      const isRadio = input.getAttribute('data-ngf-ui') === 'radio';
      const isPrice = this.#isPriceGroup(this.#groupMeta.get(key));

      if (!this.#stagedFilters.has(key)) {
        this.#stagedFilters.set(key, new Map());
      }

      const values = this.#stagedFilters.get(key);

      if (input.checked) {
        // Radio uiType groups are single-select — replace any prior selection.
        // Price is always single-select too (one staged entry → one pill),
        // regardless of the API-provided uiType.
        if (isRadio || isPrice) {
          values.clear();

          // Uncheck sibling inputs (options live in the sub-drawer)
          const scope = this.#q('[data-ngf-sub-drawer]') || this.#portal;
          if (scope) {
            for (const sibling of scope.querySelectorAll(`[data-ngf-staged-filter="${CSS.escape(key)}"]`)) {
              if (sibling !== input) {
                /** @type {HTMLInputElement} */ (sibling).checked = false;
              }
            }
          }
        }

        values.set(input.value, label);

        // Sync the price slider/inputs when a predefined radio option is selected
        if (isPrice) {
          const container = input.closest('[data-ngf-sub-body]')?.querySelector('[data-ngf-price-controls]');
          if (container) {
            const parts = String(input.value).split('-');
            const lo = parseFloat(parts[0]);
            const hiStr = parts.length > 1 ? parts[parts.length - 1].replace('+', '') : parts[0].replace('+', '');
            const hi = parseFloat(hiStr);
            if (!Number.isNaN(lo) && !Number.isNaN(hi)) {
              this.#syncPriceUI(/** @type {HTMLElement} */ (container), lo, hi);
            }
          }
        }
      } else {
        values.delete(input.value);
        if (values.size === 0) this.#stagedFilters.delete(key);
      }

      // Chrome can leave a long scrollable drawer body visually blank (the DOM
      // is intact) when a checkbox :checked state flips inside a transform-
      // positioned fixed drawer — the compositor fails to repaint the scrolled
      // layer. Force a synchronous reflow + repaint, preserving scroll position.
      this.#repaintScrollBody(input);

      // Radio options apply immediately (price buckets included) — the drawer
      // stays open so chip/count updates show live. Only the custom price range
      // (slider + min/max inputs) is staged and applied via the "Done" button.
      this.#commitFilters();
      this.#closeActiveDrawer();

      return;
    }
  }

  /**
   * Forces a synchronous reflow/repaint of the scrollable drawer body that
   * contains the given element, working around a Chrome compositing bug where
   * toggling a checkbox blanks the body. Scroll position is preserved.
   *
   * @param {HTMLElement} el
   */
  #repaintScrollBody(el) {
    const body = el.closest('[data-ngf-sub-body], [data-ngf-filter-body]');
    if (!body) return;
    const top = body.scrollTop;
    body.style.display = 'none';
    void body.offsetHeight; // force reflow while hidden
    body.style.display = '';
    body.scrollTop = top;
  }

  /**
   * Handles click events on drawer triggers, close buttons, apply/clear,
   * chip remove, clear-all, overlay, and group toggles via delegation.
   *
   * @param {Event} event
   */
  #handleClick(event) {
    const target = /** @type {HTMLElement} */ (event.target).closest(
      '[data-ngf-open-sort], [data-ngf-open-filter], [data-ngf-close-sort], [data-ngf-close-filter], ' +
      '[data-ngf-clear-sort], [data-ngf-clear-filter], ' +
      '[data-ngf-price-reset], [data-ngf-price-done], ' +
      '[data-ngf-open-group], [data-ngf-sub-back], [data-ngf-pill-group], ' +
      '[data-ngf-scroll-left], [data-ngf-scroll-right], [data-ngf-view], ' +
      '[data-ngf-remove], [data-ngf-clear], [data-ngf-overlay]'
    );
    if (!target) return;

    // Scroll the group-pills row left / right
    if (target.hasAttribute('data-ngf-scroll-left')) {
      this.#scrollPills(-1);
      return;
    }
    if (target.hasAttribute('data-ngf-scroll-right')) {
      this.#scrollPills(1);
      return;
    }

    // Grid / list view toggle (mobile) — drives the products grid columns
    if (target.hasAttribute('data-ngf-view')) {
      this.#setView(target.getAttribute('data-ngf-view') === 'list' ? 'list' : 'grid');
      return;
    }

    // Open sort drawer
    if (target.hasAttribute('data-ngf-open-sort')) {
      this.#lastTrigger = target;
      this.#openSortDrawer();
      return;
    }

    // Open filter drawer
    if (target.hasAttribute('data-ngf-open-filter')) {
      this.#lastTrigger = target;
      this.#openFilterDrawer();
      return;
    }

    // Close sort drawer
    if (target.hasAttribute('data-ngf-close-sort')) {
      this.#closeSortDrawer();
      return;
    }

    // Close filter drawer
    if (target.hasAttribute('data-ngf-close-filter')) {
      this.#closeFilterDrawer();
      return;
    }

    // Clear sort
    if (target.hasAttribute('data-ngf-clear-sort')) {
      this.#clearSort();
      return;
    }

    // Clear filters
    if (target.hasAttribute('data-ngf-clear-filter')) {
      if (this.#pillDirectOpen && this.#subOpen && this.#activeGroupKey) {
        this.#stagedFilters.delete(this.#activeGroupKey);
        this.#selectedFilters.delete(this.#activeGroupKey);
        this.#closeSubDrawer();
        this.#render(this.#filterGroups);
        this.#currentPage = 1;
        this.#refetch();
      } else {
        this.#clearFilters();
      }
      return;
    }

    // Price "Done" — apply the staged range and close the complete drawer flow.
    // A toolbar price pill opens only the sub-drawer, while the Price row inside
    // Filters opens it on top of the main filter drawer.
    // The staged entry is kept current by every radio/slider/input change, so
    // it simply commits (mousedown blurs any focused input first, firing its
    // change → staging the latest typed value before this click runs).
    if (target.hasAttribute('data-ngf-price-done')) {
      this.#commitFilters();
      if (this.#pillDirectOpen) {
        this.#closeSubDrawer();
      } else {
        this.#closeFilterDrawer();
      }
      return;
    }

    // Price "Reset" — revert the in-drawer selection to the last applied value
    // (no refetch; nothing is applied until "Done").
    if (target.hasAttribute('data-ngf-price-reset')) {
      const key = this.#activeGroupKey;
      if (key) {
        const applied = this.#selectedFilters.get(key);
        if (applied && applied.size > 0) {
          this.#stagedFilters.set(key, new Map(applied));
        } else {
          this.#stagedFilters.delete(key);
        }
        this.#renderSubDrawerBody();
      }
      return;
    }

    // Pill-group click (toolbar pill for a filter group)
    if (target.hasAttribute('data-ngf-pill-group')) {
      const key = target.getAttribute('data-ngf-pill-group') || '';
      const isBoolean = target.getAttribute('data-ngf-pill-boolean') === 'true';
      if (isBoolean) {
        this.#toggleBooleanFilter(key);
      } else {
        this.#lastTrigger = target;
        this.#openSubDrawerFromPill(key);
      }
      return;
    }

    // Open a group's sub-drawer
    if (target.hasAttribute('data-ngf-open-group')) {
      const key = target.getAttribute('data-ngf-open-group') || '';
      this.#openSubDrawer(key);
      return;
    }

    // Back from sub-drawer to the group list
    if (target.hasAttribute('data-ngf-sub-back')) {
      this.#closeSubDrawer();
      return;
    }

    // Remove single chip (directly updates committed state + refetches)
    if (target.hasAttribute('data-ngf-remove')) {
      const filterType = target.getAttribute('data-ngf-remove') || '';
      const value = target.getAttribute('data-ngf-value') || '';
      const values = this.#selectedFilters.get(filterType);

      if (values) {
        values.delete(value);
        if (values.size === 0) this.#selectedFilters.delete(filterType);
      }

      this.#render(this.#filterGroups);
      this.#currentPage = 1;
      this.#refetch();
      return;
    }

    // Clear all chips (directly clears committed state + refetches)
    if (target.hasAttribute('data-ngf-clear')) {
      this.#selectedFilters.clear();
      this.#render(this.#filterGroups);
      this.#currentPage = 1;
      this.#refetch();
      return;
    }

    // Overlay click closes whichever drawer is open
    if (target.hasAttribute('data-ngf-overlay')) {
      this.#closeActiveDrawer();
    }
  }

  // ---------------------------------------------------------------------------
  // APIM re-fetch
  // ---------------------------------------------------------------------------

  /**
   * Builds the APIM `filters` request object from committed state, mapping each
   * group's selections onto its request key (boolean groups → `['true']`,
   * node groups share `node.id`, etc.).
   *
   * @returns {Record<string, string[]>}
   */
  #buildRequestFilters() {
    /** @type {Record<string, string[]>} */
    const filters = {};

    for (const [key, values] of this.#selectedFilters) {
      if (values.size === 0) continue;

      const meta = this.#groupMeta.get(key);
      if (!meta) continue;

      const requestKey = meta.requestKey || key;
      const selected = meta.isBoolean ? ['true'] : Array.from(values.keys());

      filters[requestKey] = filters[requestKey]
        ? filters[requestKey].concat(selected)
        : selected;
    }

    return filters;
  }

  /**
   * Writes the committed sort + filter state into the address bar as
   * `requestKey=value` params (sort as `sort=`), so the current view is
   * shareable and survives a reload. Uses `replaceState` to avoid flooding
   * history on every Apply. Params the component doesn't own (e.g. `q`,
   * `preview_theme_id`, `utm_*`) are preserved.
   */
  #syncUrl() {
    const url = new URL(window.location.href);
    const sp = url.searchParams;

    // Drop every param this component manages so de-selected filters disappear.
    const managed = new Set(['sort', 'page', 'node.id', 'catalogId']);
    for (const meta of this.#groupMeta.values()) {
      if (meta.requestKey) managed.add(meta.requestKey);
    }
    for (const key of [...sp.keys()]) {
      if (managed.has(key)) sp.delete(key);
    }

    // Re-add the active sort (omit the default to keep clean URLs).
    if (this.#currentSort && this.#currentSort !== DEFAULT_SORT_VALUE) {
      sp.set('sort', this.#currentSort);
    }

    // Re-add the active filters as repeated requestKey=value params.
    const filters = this.#buildRequestFilters();
    for (const requestKey of Object.keys(filters)) {
      for (const value of filters[requestKey]) sp.append(requestKey, value);
    }

    // Re-add the page param when beyond page 1 (omit for page 1 to keep URLs
    // clean). This keeps the URL consistent with the displayed page so
    // reloads and shared links work correctly.
    if (this.#currentPage > 1) {
      sp.set('page', String(this.#currentPage));
    }

    history.replaceState(history.state, '', url);
  }

  /**
   * Rebuilds committed filter selections (chips + drawer checkboxes) from the
   * URL on the first hydration response after a reload/shared link. Matches each
   * URL value against the freshly-returned groups so the correct labels and
   * group ownership are restored (node groups share the `node.id` key, so each
   * group claims only the values present in its own options). URL values no
   * offered group claims but that the response's `filtersApplied` confirms
   * (e.g. an applied category node whose facet group is no longer offered)
   * are restored under a synthetic per-request-key group, so their chips
   * render and every re-fetch / URL sync keeps sending them.
   *
   * @param {Array<NormalisedGroup>} filterGroups
   * @param {Map<string, Map<string, { label: string, groupName: string }>>} [appliedByKey]
   *   Applied filters echoed by the response, from #appliedFiltersByRequestKey.
   */
  #restoreSelectionsFromUrl(filterGroups, appliedByKey) {
    const sp = new URLSearchParams(window.location.search);

    // Values claimed by an offered group, per request key — whatever is left
    // unclaimed falls through to the applied-filter restore below.
    /** @type {Map<string, Set<string>>} */
    const claimed = new Map();
    /** @type {(requestKey: string, value: string) => void} */
    const claim = (requestKey, value) => {
      const set = claimed.get(requestKey) || new Set();
      set.add(value);
      claimed.set(requestKey, set);
    };

    for (const group of filterGroups) {
      const urlValues = sp.getAll(group.requestKey);
      if (urlValues.length === 0) continue;

      if (group.isBoolean) {
        if (urlValues.includes('true')) {
          this.#selectedFilters.set(group.key, new Map([['true', group.name]]));
          claim(group.requestKey, 'true');
        }
        continue;
      }

      const matched = new Map();
      for (const opt of group.options || []) {
        if (urlValues.includes(opt.value)) {
          matched.set(opt.value, opt.label);
          claim(group.requestKey, opt.value);
        }
      }
      if (matched.size > 0) this.#selectedFilters.set(group.key, matched);
    }

    for (const [requestKey, appliedValues] of appliedByKey || []) {
      const urlValues = sp.getAll(requestKey);
      if (urlValues.length === 0) continue;

      const matched = new Map();
      let groupName = requestKey;
      for (const value of urlValues) {
        if (claimed.get(requestKey)?.has(value)) continue;
        const appliedOpt = appliedValues.get(value);
        if (!appliedOpt) continue;
        matched.set(value, appliedOpt.label);
        groupName = appliedOpt.groupName || groupName;
      }
      if (matched.size === 0) continue;

      // Synthetic group meta so chip removal, #buildRequestFilters and
      // #syncUrl treat these selections exactly like group-owned ones. Not
      // added to #filterGroups, so no toolbar pill / drawer row is rendered.
      const key = `applied:${requestKey}`;
      this.#groupMeta.set(key, {
        key,
        name: groupName,
        requestKey,
        uiType: 'checkbox',
        options: Array.from(matched, ([value, label]) => ({ value, label })),
        isBoolean: false,
      });
      this.#selectedFilters.set(key, matched);
    }

    // Keep staged state aligned for the next time a drawer opens.
    this.#stagedFilters = cloneFilterMap(this.#selectedFilters);
  }

  /**
   * Re-fetches products with the current committed filter and sort state.
   * Cancels any in-flight request via AbortController before starting a new one.
   */
  async #refetch() {
    // Reflect the committed state in the URL (shareable + survives reload)
    // before firing the request — every commit point routes through #refetch.
    this.#syncUrl();

    // Cancel previous in-flight request
    if (this.#abortController) {
      this.#abortController.abort();
    }
    this.#abortController = new AbortController();

    const params = new URLSearchParams(window.location.search);
    // PLP: the collection's predefined search term (metafield) wins over any
    // URL `q` param and stays out of the URL/UI entirely.
    const query = (this.#context === 'plp' && this.#baseSearchTerm)
      ? this.#baseSearchTerm
      : (params.get('q') || '');

    // Build filters object from committed state
    const filters = this.#buildRequestFilters();

    // Merge base node IDs from the collection metafield for PLP context.
    // This ensures every re-fetch (filter apply, sort change, pagination,
    // clear) includes the collection's base node IDs alongside any
    // user-selected category/department node IDs.
    if (this.#context === 'plp' && this.#baseNodeIds.length > 0) {
      filters['node.id'] = [...new Set([...this.#baseNodeIds, ...(filters['node.id'] || [])])];
    }

    /** @type {import('@theme/hydrate-callers').HydrateParams} */
    const hydrationParams = {
      query,
      page: this.#currentPage,
      productParams: {
        filters,
        sort: this.#currentSort,
        aggregations: FILTER_AGGREGATIONS,
      },
      signal: this.#abortController.signal,
    };

    // No store module renders in the 'plp' context — skip store-search there.
    // SLP keeps it: the partner-stores module re-renders (or hides) from every
    // 'slp' hydration event, so its envelope must stay populated.
    if (this.#context === 'plp') {
      hydrationParams.skipStores = true;
    }

    try {
      const caller = this.#context === 'plp' ? hydratePLP : hydrateSLP;
      const result = await caller(hydrationParams);
      // Update the global cache so late-connecting consumers see fresh data
      window.__ngHydration = result;
    } catch (error) {
      // Swallow AbortError (expected when a newer request supersedes this one)
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('[filters] refetch failed', error);
    }
  }

  // ---------------------------------------------------------------------------
  // Empty / design-mode state
  // ---------------------------------------------------------------------------

  /**
   * Hides the section on the live storefront when there is no data to display.
   * In the theme editor the element is left in place (so it can be selected),
   * but the section CSS collapses it via the toolbar's `hidden` state so no
   * empty box is shown.
   */
  #showEmptyState() {
    const toolbar = this.querySelector('[data-ngf-toolbar]');
    const skeleton = this.querySelector('[data-ngf-skeleton]');
    if (toolbar) toolbar.hidden = true;
    if (skeleton) skeleton.hidden = true;

    if (this.#isDesignMode) {
      this.hidden = false;
      return;
    }

    this.hidden = true;
  }

  /**
   * Reveals the skeleton toolbar while the initial hydration fetch is in flight,
   * so the filter/sort bar reserves its space instead of popping in. Replaced by
   * the real toolbar in #render() (or cleared by #showEmptyState() on no data).
   */
  #showLoading() {
    const toolbar = this.querySelector('[data-ngf-toolbar]');
    // Only reserve space with the skeleton on the first load. Once the real
    // toolbar has rendered, keep it in place during re-fetches (filter/sort
    // apply, pagination) instead of blinking back to the skeleton.
    if (toolbar && !toolbar.hidden) return;
    const skeleton = this.querySelector('[data-ngf-skeleton]');
    if (skeleton) skeleton.hidden = false;
    this.hidden = false;
  }
}

if (!customElements.get('hydrate-filters')) {
  customElements.define('hydrate-filters', HydrateFilters);
}
